import { ObjectModelPropertyCapability } from "~/models/objectModelProperties/objectModelPropertyCapability";
import { ObjectModelTriggerCapability } from "~/models/objectModelTriggers/objectModelTriggerCapability";
import { VisualizationDataPointCapability } from "~/models/visualizations/VisualizationDataPointCapability";
import { ConditionalFieldDefinition } from "../conditionalRules/conditionalFieldDefinition";
import { ListViewContentElementCapability } from "../listViews/elements/listViewContentElementCapability";
import { ListViewFilterElementCapability } from "../listViews/elements/listViewFilterElementCapability";
import ObjectModelAction from "../objectModelActions/objectModelAction";
import { ObjectModelActionCapability } from "../objectModelActions/objectModelActionCapability";

export enum ObjectModelConditionalFieldType {
  action = 1,
  listViewElement = 2,
  listView = 3,
  trigger = 4,
  interval=5,
  aggregateFilter = 6,
  visualization = 7,
  detailViewElement = 8,
  timeTrigger = 9,
}

export interface ObjectModelActionConditionalField extends ConditionalFieldDefinition {
  type: ObjectModelConditionalFieldType.action;
  capability?: ObjectModelActionCapability;
}
export interface ObjectModelTriggerConditionalField extends  ConditionalFieldDefinition{
  type: (ObjectModelConditionalFieldType.trigger | ObjectModelConditionalFieldType.timeTrigger);
  capability?: ObjectModelTriggerCapability;
}
export interface VisualizationConditionalField extends  ConditionalFieldDefinition{
  type: ObjectModelConditionalFieldType.visualization;
  capability?: VisualizationDataPointCapability;
}
export interface ObjectModelAggregateFilterField extends  ConditionalFieldDefinition{
  type: ObjectModelConditionalFieldType.aggregateFilter;
  capability?: ObjectModelPropertyCapability;
}
export interface ObjectModelIntervalConditionalField extends  ConditionalFieldDefinition{
  type: ObjectModelConditionalFieldType.interval;
  capability?: ObjectModelTriggerCapability;
}

export interface ListViewContentElementConditionalField extends ConditionalFieldDefinition {
  type: ObjectModelConditionalFieldType.listViewElement;
  capability?: ListViewContentElementCapability;
}

export interface ListViewFilterElementConditionalField extends ConditionalFieldDefinition {
  type: ObjectModelConditionalFieldType.listViewElement;
  capability?: ListViewFilterElementCapability;
}

// Defines the conditional fields that reference object model properties.
// They can now reference object model (account) relations as well, currently only for actions
export default class ObjectModelConditionalFields {
  static ACTION_AVAILABLE: ObjectModelActionConditionalField = {
    type: ObjectModelConditionalFieldType.action,
    capability: ObjectModelActionCapability.availabilityConditionable,
    fieldName: 'action_available',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value ? 'Available' : 'Unavailable'
  };
  static TRIGGER_AVAILABLE: ObjectModelTriggerConditionalField = {
    type: ObjectModelConditionalFieldType.trigger,
    capability: ObjectModelTriggerCapability.triggerConditionable,
    fieldName: 'trigger',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };
  static TIME_TRIGGER_AVAILABLE: ObjectModelTriggerConditionalField = {
    type: ObjectModelConditionalFieldType.timeTrigger,
    capability: ObjectModelTriggerCapability.triggerConditionable,
    fieldName: 'time_trigger',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };
  static VISUALIZATION_FILTER: VisualizationConditionalField = {
    type: ObjectModelConditionalFieldType.visualization,
    capability: VisualizationDataPointCapability.filterable,
    fieldName: 'data_point',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };
  static AGGREGATE_FILTER: ObjectModelAggregateFilterField = {
    type: ObjectModelConditionalFieldType.aggregateFilter,
    capability: ObjectModelPropertyCapability.aggregatable,
    fieldName: 'filter',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };
  static REFERENCED_ICON_ID: ListViewContentElementConditionalField = {
    type: ObjectModelConditionalFieldType.listViewElement,
    capability: ListViewContentElementCapability.iconImageReferencable,
    fieldName: 'referenced_icon_id',
    defaultConditionalValue: null,
    friendlyName: (r) => r.conditional_value
  };
  static INTERVAL_END_CONDITION: ObjectModelIntervalConditionalField = {
    type: ObjectModelConditionalFieldType.interval,
    fieldName: 'end_condition',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };
  static ITEM_INCLUSION_CONDITION: ListViewFilterElementConditionalField = {
    type: ObjectModelConditionalFieldType.listViewElement,
    capability: ListViewFilterElementCapability.conditionable,
    fieldName: 'item_inclusion_condition',
    defaultConditionalValue: null,
    friendlyName: (r) => r.conditional_value
  };
  static PREDEFINED_FILTER: ConditionalFieldDefinition = {
    fieldName: 'predefined_filter',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };

  static ENTITY_TYPE_OBJECT_MODEL_ASSOCIATION: ConditionalFieldDefinition = {
    fieldName: 'entity_type_object_model_association',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };

  static USER_TYPE_OBJECT_MODEL_ASSOCIATION: ConditionalFieldDefinition = {
    fieldName: 'user_type_object_model_association',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };

  static FORM_OBJECT_MODEL_PREREQUISITE: ConditionalFieldDefinition = {
    fieldName: 'object_model_prerequisite',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };

  static LIMIT: ConditionalFieldDefinition = {
    fieldName: 'limit',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  }

  static DASHBOARD_CONDITIONAL_LINKED_DETAIL_VIEW: ConditionalFieldDefinition = {
    fieldName: 'dashboard_conditional_linked_detail_view',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  }

  static CERTIFICATE_MAPPED_MODEL_LIST_FILTER: ConditionalFieldDefinition = {
    fieldName: 'list_filter',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value,
  };

  static LOOKUP_VALIDATION_RULE: ConditionalFieldDefinition = {
    fieldName: 'lookup_validation_rule',
    defaultConditionalValue: '',
    friendlyName: (r) => r.conditional_value ? 'is valid' : 'is not valid'
  };
  
  static SHOW_VALIDATION_RULE: ConditionalFieldDefinition = {
    fieldName: 'show_validation_rule',
    defaultConditionalValue: '',
    friendlyName: (r) => r.conditional_value ? 'is valid' : 'is not valid'
  };

  static DETAIL_VIEW_ELEMENT_VISIBILITY: ConditionalFieldDefinition = {
    fieldName: 'visible',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value
  };

  static ACTION_FIELDS: ObjectModelActionConditionalField[] = [
    ObjectModelConditionalFields.ACTION_AVAILABLE,
  ];

  static TRIGGER_FIELDS: ObjectModelTriggerConditionalField[] = [
    ObjectModelConditionalFields.TRIGGER_AVAILABLE,
    ObjectModelConditionalFields.TIME_TRIGGER_AVAILABLE,
  ];

  static VISUALIZATION_FIELDS: VisualizationConditionalField[] = [
    ObjectModelConditionalFields.VISUALIZATION_FILTER,
  ];

  static AGGREGATE_FIELDS: ObjectModelAggregateFilterField[] = [
    ObjectModelConditionalFields.AGGREGATE_FILTER,
  ];

  static INTERVAL_FIELDS: ObjectModelIntervalConditionalField[] = [
    ObjectModelConditionalFields.INTERVAL_END_CONDITION,
  ];

  static LIST_VIEW_FILTER_ELEMENT_FIELDS: ListViewFilterElementConditionalField[] = [
    ObjectModelConditionalFields.ITEM_INCLUSION_CONDITION,
  ];

  static LIST_VIEW_CONTENT_ELEMENT_FIELDS: ListViewContentElementConditionalField[] = [
    ObjectModelConditionalFields.REFERENCED_ICON_ID,
  ];

  static LIST_VIEW_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.PREDEFINED_FILTER,
  ];

  static ENTITY_TYPE_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.ENTITY_TYPE_OBJECT_MODEL_ASSOCIATION
  ];

  static USER_TYPE_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.USER_TYPE_OBJECT_MODEL_ASSOCIATION
  ];

  static LIMIT_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.LIMIT
  ]
  
  static FORM_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.FORM_OBJECT_MODEL_PREREQUISITE,
  ];

  static DASHBOARD_CONDITIONAL_LINKED_DETAIL_VIEW_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.DASHBOARD_CONDITIONAL_LINKED_DETAIL_VIEW,
  ];

  static CERTIFICATE_MAPPED_MDOEL_RELATION_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.CERTIFICATE_MAPPED_MODEL_LIST_FILTER,
  ];

  static VALIDATION_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.LOOKUP_VALIDATION_RULE,
    ObjectModelConditionalFields.SHOW_VALIDATION_RULE
  ];

  static DETAIL_VIEW_ELEMENT_FIELDS: ConditionalFieldDefinition[] = [
    ObjectModelConditionalFields.DETAIL_VIEW_ELEMENT_VISIBILITY,
  ];

  static actionCan(definition: ObjectModelActionConditionalField, action: ObjectModelAction): boolean {
    if (definition.type !== ObjectModelConditionalFieldType.action) {
      return false;
    } else if (definition.capability === null || definition.capability === undefined) {
      return true;
    } else {
      return action.can(<ObjectModelActionCapability>definition.capability);
    }
  }
}